/**
 * Phase 3a 후속 (Issue #1143) — WebAuthn AAGUID → provider 이름 매핑.
 * Phase 3a 후속 (Issue #1154) — 디바이스 호환성 메타데이터 확장.
 *
 * 데이터 출처: https://github.com/passkeydeveloper/passkey-authenticator-aaguids
 * (MIT License, aaguid.json — fetch 시점: 2026-05)
 *
 * 사용처:
 *   - Settings > Security > PasskeyManagement — provider 식별 표시 (#1143)
 *   - LoginMasterPW maybeTryPasskey — 디바이스 호환성 판단 (#1154)
 *
 * 매핑 메타 구조:
 *   - name: 표시용 provider 이름
 *   - platforms: 호환되는 parsePlatform() 결과값 배열 (parsePlatform: Mac/iPhone/iPad/Windows/Android/Linux/Device)
 *   - browsers: 호환되는 parseBrowser() 결과값 배열 (parseBrowser: Chrome/Edge/Whale/Samsung Internet/Firefox/Opera/Safari/Browser)
 *
 * 매핑 갱신 정책 (B1 점진 적용):
 * - 신규 AAGUID 미매핑 → null 반환 → caller 가 'Unknown provider' 표시 또는 비호환 판정
 * - 정기 (분기별) passkeydeveloper repo 비교 후 manual 갱신 권고
 *
 * NULL AAGUID (legacy / zero AAGUID 정규화) 처리는 caller 책임.
 */

const AAGUID_TO_PROVIDER_META = {
  // Apple — iCloud Keychain (Apple 디바이스 전용. iCloud Passwords on Windows 는 password 만 sync, passkey 미지원)
  // 근거: https://www.techlicious.com/blog/how-to-use-apple-keychain-passwords-passkeys-on-windows/
  //       "iCloud Passwords app on Windows doesn't include Passkeys"
  // [#1161] PRF minOsVersion — Corbado 공식 매트릭스 검증:
  //   https://www.corbado.com/blog/passkeys-prf-webauthn
  //   "PRF support was enabled in iOS 18.4+ and is available on Safari 18+"
  //   macOS 15+ Safari/Chrome (Mac local secure enclave 별도, 본 매핑은 iCloud Keychain)
  //   본 매핑은 시작 버전 18.0 기준 — 18.0-18.3 cross-device data-loss bug 는 표준 영역 외.
  'fbfc3007-154e-4ecc-8c0b-6e020557d7bd': {
    name: 'Apple Passwords',
    platforms: ['Mac', 'iPhone', 'iPad'],
    browsers: [],
    minOsVersion: { Mac: '15.0', iPhone: '18.0', iPad: '18.0' },
  },
  'dd4ec289-e01d-41c9-bb89-70fa845d4bf2': {
    name: 'iCloud Keychain (Managed)',
    platforms: ['Mac', 'iPhone', 'iPad'],
    browsers: [],
    minOsVersion: { Mac: '15.0', iPhone: '18.0', iPad: '18.0' },
  },
  // Google — Chrome sync (모든 platform)
  // [#1161] PRF minOsVersion — Android 9+ Google Password Manager (Corbado: "All passkeys stored in Google Password Manager have PRF support")
  // Chrome browser 자체 PRF 지원은 일찍부터 — minBrowserVersion 미정의 (옵티미스틱)
  // [Phase 3a hotfix] platforms+browsers 둘 다 정의 → AND 로직 적용 (deviceCompatibility.js).
  //   - Android Native + GPM = primary 호환 (Samsung Internet 포함, Android Credential Manager 동일 접근)
  //   - 데스크탑 Chrome 은 Chrome sync 활성화 + passkey sync 옵션 켜짐 조건부 — frontend 런타임 확인 불가
  //     → false positive 회피를 위해 매핑에서 제외 (사용자 수동 재시도 UX 트레이드오프 수용)
  'ea9b8d66-4d01-1d21-3ce4-b6b48cb575d4': {
    name: 'Google Password Manager',
    platforms: ['Android'],
    browsers: ['Chrome', 'Samsung Internet'],
    minOsVersion: { Android: '9' },
  },
  // Chrome on Mac — Mac local secure enclave 저장. profile 단위, 다른 디바이스 sync X.
  // 근거: https://www.corbado.com/glossary/chrome-on-mac
  //       "Chrome on Mac can use passkeys stored locally in the secure enclave"
  //       "If the Chrome profile is deleted or the macOS devices is lost, passkeys cannot be recovered"
  // [#1161] PRF minOsVersion: macOS 15+ (Corbado 매트릭스 추정 — Chrome 132+ 동시 요구)
  'adce0002-35bc-c60a-648b-0b25f1f05503': {
    name: 'Chrome on Mac',
    platforms: ['Mac'],
    browsers: [],
    minOsVersion: { Mac: '15.0' },
  },
  'b5397666-4885-aa6b-cebf-e52262a439a2': {
    name: 'Chromium Browser',
    platforms: [],
    browsers: ['Chrome'],
  },
  // Microsoft — Windows Hello (3개 AAGUID 변종)
  '08987058-cadc-4b81-b6e1-30de50dcbe96': {
    name: 'Windows Hello',
    platforms: ['Windows'],
    browsers: [],
  },
  '9ddd1817-af5a-4672-a2b9-3e3dd95000a9': {
    name: 'Windows Hello',
    platforms: ['Windows'],
    browsers: [],
  },
  '6028b017-b1d4-4c02-b4b3-afcdafc96bb2': {
    name: 'Windows Hello',
    platforms: ['Windows'],
    browsers: [],
  },
  // Edge on Mac — Mac profile 또는 iCloud Keychain (config). Windows Edge 는 다른 AAGUID.
  // 근거: https://learn.microsoft.com/en-us/deployedge/microsoft-edge-browser-policies/createpasskeysinicloudkeychain
  // [#1161] PRF minOsVersion: macOS 15+ (iCloud Keychain config 경로). minBrowserVersion (Edge) 는 공식 출처 부재 — null
  '771b48fd-d3d4-4f74-9232-fc157ab0507a': {
    name: 'Edge on Mac',
    platforms: ['Mac'],
    browsers: [],
    minOsVersion: { Mac: '15.0' },
  },
  // Samsung Pass — Android 전용
  '53414d53-554e-4700-0000-000000000000': {
    name: 'Samsung Pass',
    platforms: ['Android'],
    browsers: [],
  },
  // 3rd party password managers — 모든 platform sync
  'bada5566-a7aa-401f-bd96-45619a55120d': {
    name: '1Password',
    platforms: ['Mac', 'iPhone', 'iPad', 'Windows', 'Android', 'Linux'],
    browsers: [],
  },
  'd548826e-79b4-db40-a3d8-11116f7e8349': {
    name: 'Bitwarden',
    platforms: ['Mac', 'iPhone', 'iPad', 'Windows', 'Android', 'Linux'],
    browsers: [],
  },
  '531126d6-e717-415c-9320-3d9aa6981239': {
    name: 'Dashlane',
    platforms: ['Mac', 'iPhone', 'iPad', 'Windows', 'Android', 'Linux'],
    browsers: [],
  },
  'b84e4048-15dc-4dd0-8640-f4f60813c8af': {
    name: 'NordPass',
    platforms: ['Mac', 'iPhone', 'iPad', 'Windows', 'Android', 'Linux'],
    browsers: [],
  },
  '0ea242b4-43c4-4a1b-8b17-dd6d0b6baec6': {
    name: 'Keeper',
    platforms: ['Mac', 'iPhone', 'iPad', 'Windows', 'Android', 'Linux'],
    browsers: [],
  },
  'f3809540-7f14-49c1-a8b3-8f813b225541': {
    name: 'Enpass',
    platforms: ['Mac', 'iPhone', 'iPad', 'Windows', 'Android', 'Linux'],
    browsers: [],
  },
  'a11a5faa-9f32-4b8c-8c5d-2f7d13e8c942': {
    name: 'AliasVault',
    platforms: ['Mac', 'iPhone', 'iPad', 'Windows', 'Android', 'Linux'],
    browsers: [],
  },
  // Sésame — Apple 전용 (iOS 11+/macOS Catalina+/watchOS 6+). Android/Windows/Linux 미지원.
  // 근거: https://apps.apple.com/us/app/s%C3%A9same-secure-password-manager/id1370928630
  //       "exclusively designed for the Apple ecosystem"
  '891494da-2c90-4d31-a9cd-4eab0aed1309': {
    name: 'Sésame',
    platforms: ['Mac', 'iPhone', 'iPad'],
    browsers: [],
  },
  '39a5647e-1853-446c-a1f6-a79bae9f5bc7': {
    name: 'IDmelon',
    platforms: ['Mac', 'iPhone', 'iPad', 'Windows', 'Android', 'Linux'],
    browsers: [],
  },
}

/**
 * AAGUID를 provider 이름으로 변환 (#1143 호환 유지).
 *
 * @param {string|null|undefined} aaguid - 36자 lowercase UUID string (backend 정규화 결과)
 * @returns {string|null} provider 이름. NULL/매핑 미존재 시 null
 */
function aaguidToProvider(aaguid) {
  if (!aaguid) return null
  return AAGUID_TO_PROVIDER_META[aaguid]?.name || null
}

/**
 * AAGUID 의 호환성 메타 반환 (#1154 — deviceCompatibility 에서 사용).
 *
 * @param {string|null|undefined} aaguid
 * @returns {{name: string, platforms: string[], browsers: string[]}|null}
 */
export function getProviderMeta(aaguid) {
  if (!aaguid) return null
  return AAGUID_TO_PROVIDER_META[aaguid] || null
}

export default aaguidToProvider
